import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";

import AppSidebarV2 from "../components/layout/AppSidebarV2";
import { useAuth } from "../auth/authStore";
import { diaryApi } from "../api/diaryApi";
import { psychologistsApi } from "../api/psychologistsApi";
import { useNotifications } from "../notifications/notificationsStore";
import { useToast } from "../ui/toast/ToastProvider";
import { DIARY_MOOD_OPTIONS, diaryMoodBadge } from "../utils/diary";

function roleLabel(role) {
  const normalized = String(role || "").replace(/^ROLE_/, "").toUpperCase();
  if (normalized === "ADMIN") return "Администратор";
  if (normalized === "PSYCHOLOGIST") return "Психолог";
  if (normalized === "MODERATOR") return "Модератор";
  return "Клиент";
}

function parseDate(raw) {
  if (!raw) return null;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatRuDate(raw, options) {
  const parsed = parseDate(raw);
  if (!parsed) return "—";
  return new Intl.DateTimeFormat("ru-RU", options).format(parsed);
}

function formatRuDateTime(raw) {
  return formatRuDate(raw, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatEntryDate(entry) {
  return formatRuDate(entry?.entryDate || entry?.createdAt, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatWeekday(raw) {
  return formatRuDate(raw, { weekday: "long" });
}

function capitalizeFirst(value) {
  const text = String(value || "").trim();
  return text ? `${text.slice(0, 1).toUpperCase()}${text.slice(1)}` : "";
}

function startOfLocalDay(raw) {
  const parsed = parseDate(raw);
  if (!parsed) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

function addDays(raw, amount = 0) {
  const base = startOfLocalDay(raw);
  if (!base) return null;
  const next = new Date(base);
  next.setDate(next.getDate() + Number(amount || 0));
  return new Date(next.getFullYear(), next.getMonth(), next.getDate());
}

function dayKey(raw) {
  const parsed = startOfLocalDay(raw);
  if (!parsed) return "";
  return [
    parsed.getFullYear(),
    String(parsed.getMonth() + 1).padStart(2, "0"),
    String(parsed.getDate()).padStart(2, "0"),
  ].join("-");
}

function formatMonthTitle(raw) {
  const parsed = startOfLocalDay(raw);
  if (!parsed) return "";
  return capitalizeFirst(
    new Intl.DateTimeFormat("ru-RU", {
      month: "long",
      year: "numeric",
    }).format(parsed)
  );
}

function buildCalendarCells(rawMonth) {
  const monthStart = startOfLocalDay(rawMonth);
  if (!monthStart) return [];

  const normalizedMonthStart = new Date(monthStart.getFullYear(), monthStart.getMonth(), 1);
  const mondayFirstOffset = (normalizedMonthStart.getDay() + 6) % 7;
  const gridStart = addDays(normalizedMonthStart, -mondayFirstOffset);

  return Array.from({ length: 42 }, (_, index) => {
    const date = addDays(gridStart, index);
    return {
      key: dayKey(date),
      date,
      day: date.getDate(),
      inMonth: date.getMonth() === normalizedMonthStart.getMonth(),
    };
  });
}

function shiftMonth(rawMonth, delta = 0) {
  const parsed = startOfLocalDay(rawMonth);
  if (!parsed) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth() + Number(delta || 0), 1);
}

function initials(value = "") {
  const parts = String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);
  const joined = parts.map((part) => part[0]).join("");
  return joined ? joined.toUpperCase() : "БД";
}

function dayInputValue(raw) {
  const parsed = parseDate(raw);
  if (!parsed) {
    const today = new Date();
    return dayKey(today);
  }
  return dayKey(parsed);
}

function toDraft(entry = {}) {
  return {
    title: String(entry.title || ""),
    content: String(entry.content || entry.text || ""),
    moodScore: Number.isFinite(Number(entry.moodScore)) ? Number(entry.moodScore) : null,
    entryDate: dayInputValue(entry.entryDate || entry.createdAt),
    shareWithGrantedPsychologists: Boolean(entry.shareWithGrantedPsychologists),
  };
}

function createEmptyDraft() {
  return {
    title: "",
    content: "",
    moodScore: null,
    entryDate: dayInputValue(null),
    shareWithGrantedPsychologists: false,
  };
}

function trimText(value, max = 220) {
  const text = String(value || "").trim();
  if (!text) return "";
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).trimEnd()}…`;
}

function extractItems(raw) {
  if (Array.isArray(raw)) return raw;
  const obj = raw && typeof raw === "object" ? raw : {};
  if (Array.isArray(obj.content)) return obj.content;
  if (Array.isArray(obj.items)) return obj.items;
  if (Array.isArray(obj.items?.content)) return obj.items.content;
  if (Array.isArray(obj.item?.content)) return obj.item.content;
  if (Array.isArray(obj.item?.items)) return obj.item.items;
  return [];
}

function normalizePsychologistOption(item = {}) {
  const raw = item && typeof item === "object" ? item : {};
  const id = raw.userId ?? raw.psychologistUserId ?? raw.user?.id ?? raw.id ?? null;
  if (!id) return null;
  return {
    id,
    name: String(raw.name || raw.fullName || raw.displayName || raw.user?.name || raw.email || `Психолог #${id}`).trim(),
  };
}

function sortEntries(entries = []) {
  return [...entries].sort((a, b) => {
    const aDate =
      parseDate(a.entryDate)?.getTime() ||
      parseDate(a.updatedAt)?.getTime() ||
      parseDate(a.createdAt)?.getTime() ||
      0;
    const bDate =
      parseDate(b.entryDate)?.getTime() ||
      parseDate(b.updatedAt)?.getTime() ||
      parseDate(b.createdAt)?.getTime() ||
      0;
    return bDate - aDate;
  });
}

export default function Diary() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { me, logout } = useAuth();
  const { unreadNotificationsCount = 0 } = useNotifications();
  const toast = useToast();

  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);
  const [entries, setEntries] = React.useState([]);
  const [entriesLoading, setEntriesLoading] = React.useState(true);
  const [entriesError, setEntriesError] = React.useState("");
  const [diaryUnavailable, setDiaryUnavailable] = React.useState(false);
  const [isCreating, setIsCreating] = React.useState(false);
  const [selectedEntryId, setSelectedEntryId] = React.useState(null);
  const [draft, setDraft] = React.useState(createEmptyDraft);
  const [accesses, setAccesses] = React.useState([]);
  const [accessesLoading, setAccessesLoading] = React.useState(true);
  const [psychologists, setPsychologists] = React.useState([]);
  const [psychologistsLoading, setPsychologistsLoading] = React.useState(false);
  const [grantPsychologistId, setGrantPsychologistId] = React.useState("");
  const [grantComment, setGrantComment] = React.useState("");
  const [grantingAccess, setGrantingAccess] = React.useState(false);
  const [revokingAccessId, setRevokingAccessId] = React.useState("");
  const [bulkSharing, setBulkSharing] = React.useState(false);
  const [shareToolsOpen, setShareToolsOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [calendarMonth, setCalendarMonth] = React.useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const userName = me?.name || me?.fullName || me?.login || me?.email || "Пользователь";
  const canManageLiterature = ["ADMIN", "PSYCHOLOGIST", "MODERATOR"].includes(
    String(me?.role || "").replace(/^ROLE_/, "").toUpperCase()
  );

  const sortedEntries = React.useMemo(() => sortEntries(entries), [entries]);
  const selectedEntry = React.useMemo(
    () => sortedEntries.find((entry) => String(entry.id) === String(selectedEntryId)) || null,
    [sortedEntries, selectedEntryId]
  );
  const userInitials = React.useMemo(() => initials(userName), [userName]);

  const availablePsychologists = React.useMemo(() => {
    const granted = new Set(accesses.map((item) => String(item.psychologistUserId)));
    return psychologists.filter((item) => !granted.has(String(item.id)));
  }, [accesses, psychologists]);

  const entriesByDay = React.useMemo(() => {
    const grouped = new Map();
    sortedEntries.forEach((entry) => {
      const key = dayKey(entry?.entryDate || entry?.createdAt || entry?.updatedAt);
      if (!key) return;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(entry);
    });
    return grouped;
  }, [sortedEntries]);

  const todayKey = React.useMemo(() => dayKey(new Date()), []);

  const calendarCells = React.useMemo(() => buildCalendarCells(calendarMonth), [calendarMonth]);
  const calendarTitle = React.useMemo(() => formatMonthTitle(calendarMonth), [calendarMonth]);

  const streakAnchor = React.useMemo(() => {
    if (entriesByDay.has(todayKey)) return startOfLocalDay(new Date());
    return startOfLocalDay(sortedEntries[0]?.entryDate || sortedEntries[0]?.createdAt || new Date());
  }, [entriesByDay, sortedEntries, todayKey]);

  const streakCount = React.useMemo(() => {
    if (!streakAnchor) return 0;
    let count = 0;
    let cursor = streakAnchor;
    while (cursor && entriesByDay.has(dayKey(cursor))) {
      count += 1;
      cursor = addDays(cursor, -1);
    }
    return count;
  }, [entriesByDay, streakAnchor]);

  const streakWindow = React.useMemo(() => {
    const anchor = streakAnchor || startOfLocalDay(new Date());
    return Array.from({ length: 7 }, (_, index) => {
      const date = addDays(anchor, index - 6);
      return {
        key: dayKey(date),
        date,
        label: capitalizeFirst(formatRuDate(date, { weekday: "short" }).replace(".", "")).slice(0, 2),
        hasEntry: entriesByDay.has(dayKey(date)),
        isToday: dayKey(date) === todayKey,
      };
    });
  }, [entriesByDay, streakAnchor, todayKey]);

  const handleLogout = React.useCallback(async () => {
    await logout();
    navigate("/login", { replace: true });
  }, [logout, navigate]);

  const loadEntries = React.useCallback(async () => {
    setEntriesLoading(true);
    setEntriesError("");
    try {
      const page = await diaryApi.listEntries({ page: 0, size: 100 });
      setEntries(page.items);
      setDiaryUnavailable(false);
    } catch (error) {
      setEntries([]);
      setEntriesError(error?.message || "Не удалось загрузить дневник");
      setDiaryUnavailable(Boolean(error?.diaryDisabled || error?.diaryUnavailable || Number(error?.status) === 404));
    } finally {
      setEntriesLoading(false);
    }
  }, []);

  const loadAccesses = React.useCallback(async () => {
    setAccessesLoading(true);
    try {
      const items = await diaryApi.listAccesses();
      setAccesses(items);
    } catch {
      setAccesses([]);
    } finally {
      setAccessesLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  React.useEffect(() => {
    if (diaryUnavailable) {
      setAccesses([]);
      setAccessesLoading(false);
      return;
    }
    loadAccesses();
  }, [diaryUnavailable, loadAccesses]);

  React.useEffect(() => {
    if (diaryUnavailable) return;
    let cancelled = false;
    setPsychologistsLoading(true);
    psychologistsApi
      .list({ page: 0, size: 200 })
      .then((raw) => {
        if (cancelled) return;
        const normalized = extractItems(raw).map(normalizePsychologistOption).filter(Boolean);
        setPsychologists(normalized);
      })
      .catch(() => {
        if (!cancelled) setPsychologists([]);
      })
      .finally(() => {
        if (!cancelled) setPsychologistsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [diaryUnavailable]);

  React.useEffect(() => {
    const shouldCompose = searchParams.get("compose") === "1";
    if (!shouldCompose) return;

    setIsCreating(true);
    setSelectedEntryId(null);
    setDraft(createEmptyDraft());

    const next = new URLSearchParams(searchParams);
    next.delete("compose");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  React.useEffect(() => {
    const referenceDate = selectedEntry?.entryDate || selectedEntry?.createdAt;
    const parsed = startOfLocalDay(referenceDate);
    if (!parsed) return;
    setCalendarMonth((prev) => {
      if (prev.getFullYear() === parsed.getFullYear() && prev.getMonth() === parsed.getMonth()) {
        return prev;
      }
      return new Date(parsed.getFullYear(), parsed.getMonth(), 1);
    });
  }, [selectedEntry]);

  React.useEffect(() => {
    if (isCreating) return;
    if (!sortedEntries.length) {
      setIsCreating(true);
      setSelectedEntryId(null);
      setDraft(createEmptyDraft());
      return;
    }
    if (selectedEntryId && sortedEntries.some((entry) => String(entry.id) === String(selectedEntryId))) return;

    const first = sortedEntries[0];
    setSelectedEntryId(first.id);
    setDraft(toDraft(first));
  }, [sortedEntries, selectedEntryId, isCreating]);

  const handleSelectEntry = React.useCallback((entry) => {
    if (!entry) return;
    setIsCreating(false);
    setSelectedEntryId(entry.id);
    setDraft(toDraft(entry));
  }, []);

  const handleCreate = React.useCallback(() => {
    setIsCreating(true);
    setSelectedEntryId(null);
    setDraft(createEmptyDraft());
  }, []);

  const handleResetDraft = React.useCallback(() => {
    if (isCreating) {
      setDraft(createEmptyDraft());
      return;
    }
    if (selectedEntry) {
      setDraft(toDraft(selectedEntry));
    }
  }, [isCreating, selectedEntry]);

  const handleSave = async (event) => {
    event.preventDefault();
    if (saving) return;
    if (diaryUnavailable) {
      toast.error("Дневник недоступен: модуль отключен или не подключен на сервере");
      return;
    }

    const content = String(draft.content || "").trim();
    if (!content) {
      toast.error("Добавьте текст записи");
      return;
    }

    const payload = {
      title: String(draft.title || "").trim(),
      content,
      moodScore: draft.moodScore,
      entryDate: draft.entryDate,
      shareWithGrantedPsychologists: draft.shareWithGrantedPsychologists,
    };

    setSaving(true);
    try {
      const shouldCreate = isCreating || !String(selectedEntryId || "").trim() || !selectedEntry;
      const saved = shouldCreate
        ? await diaryApi.createEntry(payload)
        : await diaryApi.updateEntry(selectedEntryId, payload);

      setEntries((prev) => {
        const next = prev.filter((entry) => String(entry.id) !== String(saved.id));
        next.push(saved);
        return sortEntries(next);
      });
      setIsCreating(false);
      setSelectedEntryId(saved.id);
      setDraft(toDraft(saved));
      toast.success(shouldCreate ? "Запись создана" : "Изменения сохранены");
    } catch (error) {
      toast.error(error?.message || "Не удалось сохранить запись");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entry = null) => {
    const targetId = String(entry?.id || selectedEntryId || "").trim();
    if (!targetId || deleting) return;
    if (diaryUnavailable) {
      toast.error("Дневник недоступен: модуль отключен или не подключен на сервере");
      return;
    }
    const confirmed = window.confirm("Удалить эту запись из дневника?");
    if (!confirmed) return;

    setDeleting(true);
    try {
      const remainingEntries = sortedEntries.filter((existing) => String(existing.id) !== targetId);
      await diaryApi.deleteEntry(targetId);
      setEntries((prev) => prev.filter((existing) => String(existing.id) !== targetId));
      if (String(selectedEntryId || "") === targetId) {
        setSelectedEntryId(null);
        if (remainingEntries.length) {
          setIsCreating(false);
          setSelectedEntryId(remainingEntries[0].id);
          setDraft(toDraft(remainingEntries[0]));
        } else {
          setIsCreating(true);
          setDraft(createEmptyDraft());
        }
      }
      toast.success("Запись удалена");
    } catch (error) {
      toast.error(error?.message || "Не удалось удалить запись");
    } finally {
      setDeleting(false);
    }
  };

  const handleGrantAccess = async () => {
    const psychologistUserId = String(grantPsychologistId || "").trim();
    if (!psychologistUserId) {
      toast.error("Выберите психолога");
      return;
    }
    setGrantingAccess(true);
    try {
      await diaryApi.grantAccess(psychologistUserId, grantComment);
      setGrantPsychologistId("");
      setGrantComment("");
      await loadAccesses();
      toast.success("Доступ к дневнику выдан");
    } catch (error) {
      toast.error(error?.message || "Не удалось выдать доступ");
    } finally {
      setGrantingAccess(false);
    }
  };

  const handleRevokeAccess = async (psychologistUserId) => {
    const id = String(psychologistUserId || "").trim();
    if (!id) return;
    setRevokingAccessId(id);
    try {
      await diaryApi.revokeAccess(id);
      await loadAccesses();
      toast.success("Доступ отозван");
    } catch (error) {
      toast.error(error?.message || "Не удалось отозвать доступ");
    } finally {
      setRevokingAccessId("");
    }
  };

  const handleShareAllEntries = async (shared) => {
    if (!sortedEntries.length || bulkSharing) return;
    setBulkSharing(true);
    try {
      const updated = [];
      for (const entry of sortedEntries) {
        const saved = await diaryApi.updateEntry(entry.id, {
          ...entry,
          shareWithGrantedPsychologists: shared,
        });
        updated.push(saved);
      }
      setEntries(sortEntries(updated));
      const current = updated.find((entry) => String(entry.id) === String(selectedEntryId));
      if (current) setDraft(toDraft(current));
      toast.success(shared ? "Все записи открыты выбранным психологам" : "Все записи снова личные");
    } catch (error) {
      toast.error(error?.message || "Не удалось изменить доступ к записям");
    } finally {
      setBulkSharing(false);
    }
  };

  const handleToggleEntryShare = React.useCallback(
    async (entry) => {
      if (!entry?.id || saving || deleting) return;
      try {
        const saved = await diaryApi.updateEntry(entry.id, {
          title: String(entry.title || "").trim(),
          content: String(entry.content || entry.text || "").trim(),
          moodScore: Number.isFinite(Number(entry.moodScore)) ? Number(entry.moodScore) : null,
          entryDate: dayInputValue(entry.entryDate || entry.createdAt),
          shareWithGrantedPsychologists: !entry.shareWithGrantedPsychologists,
        });
        setEntries((prev) => {
          const next = prev.filter((item) => String(item.id) !== String(saved.id));
          next.push(saved);
          return sortEntries(next);
        });
        if (String(selectedEntryId || "") === String(saved.id)) {
          setDraft(toDraft(saved));
        }
        toast.success(saved.shareWithGrantedPsychologists ? "Запись открыта психологу" : "Запись снова личная");
      } catch (error) {
        toast.error(error?.message || "Не удалось изменить доступ к записи");
      }
    },
    [deleting, saving, selectedEntryId, toast]
  );

  const handleSelectCalendarDay = React.useCallback(
    (cell) => {
      const key = dayKey(cell?.date);
      if (!key) return;
      const matchingEntries = entriesByDay.get(key) || [];
      if (matchingEntries.length) {
        handleSelectEntry(matchingEntries[0]);
        return;
      }
      setIsCreating(true);
      setSelectedEntryId(null);
      setDraft({
        ...createEmptyDraft(),
        entryDate: dayInputValue(cell.date),
      });
    },
    [entriesByDay, handleSelectEntry]
  );

  return (
    <div className="client-dashboard-v2 podcasts-hub">
      <Helmet prioritizeSeoTags>
        <title>Дневник клиента | Бюро Души</title>
        <meta
          name="description"
          content="Личный дневник состояния: записи, настроения, история чтения психологом и контроль доступа."
        />
      </Helmet>

      <div className="client-diary-v3">
        <div className="shell shell--shared podcasts-shell">
          <AppSidebarV2
            activeKey="diary"
            mobileOpen={mobileSidebarOpen}
            onCloseMobile={() => setMobileSidebarOpen(false)}
            userName={userName}
            roleLabel={roleLabel(me?.role)}
            canOpenLiteratureEditor={canManageLiterature}
            notificationsBadge={unreadNotificationsCount}
            onLogout={handleLogout}
          />

          <button
            className={`podcasts-overlay ${mobileSidebarOpen ? "is-open" : ""}`}
            type="button"
            onClick={() => setMobileSidebarOpen(false)}
            aria-label="Закрыть меню"
          />

          <main className="main podcasts-main cd-main">
            <div className="topbar client-diary-v3__topbar">
              <div className="topbar-left client-diary-v3__topbar-left">
                <button
                  type="button"
                  className="btn-icon topbar-hamburger"
                  onClick={() => setMobileSidebarOpen(true)}
                  aria-label="Открыть меню"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <line x1="3" y1="12" x2="21" y2="12" />
                    <line x1="3" y1="18" x2="21" y2="18" />
                  </svg>
                </button>
                <div className="client-diary-v3__topbar-title-wrap">
                  <div className="topbar-title client-diary-v3__topbar-title">Дневник</div>
                </div>
              </div>

              <div className="topbar-right client-diary-v3__topbar-right">
                {!isCreating ? (
                  <button
                    type="button"
                    className="btn-sm primary"
                    onClick={handleCreate}
                    disabled={diaryUnavailable}
                    title={diaryUnavailable ? "Дневник недоступен на сервере" : undefined}
                  >
                    Новая запись
                  </button>
                ) : null}
              </div>
            </div>

            <div className="content client-diary-v3__content">
              <div className="client-diary-v3__layout">
                <section className="client-diary-v3__main">
                  <form className="client-diary-v3__composer" onSubmit={handleSave}>
                  <div className="client-diary-v3__composer-head">
                    <div className="client-diary-v3__composer-avatar">{userInitials}</div>
                    <div className="client-diary-v3__composer-meta">
                      <div className="client-diary-v3__composer-name">
                        {isCreating ? "Новая запись" : draft.title || "Редактирование записи"}
                      </div>
                      <div className="client-diary-v3__composer-date">
                        {capitalizeFirst(
                          formatRuDate(draft.entryDate, {
                            weekday: "long",
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          })
                        )}
                      </div>
                    </div>
                    {!isCreating && selectedEntry ? (
                      <button
                        type="button"
                        className="client-diary-v3__composer-delete"
                        onClick={() => handleDelete(selectedEntry)}
                        disabled={deleting || saving}
                      >
                        {deleting ? "Удаление…" : "Удалить"}
                      </button>
                    ) : null}
                  </div>

                  <div className="client-diary-v3__mood-row">
                    <span className="client-diary-v3__mood-label">Настроение</span>
                    <div className="client-diary-v3__mood-grid">
                      {DIARY_MOOD_OPTIONS.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          className={`client-diary-v3__mood-btn ${draft.moodScore === option.score ? "is-selected" : ""}`}
                          title={option.label}
                          onClick={() =>
                            setDraft((prev) => ({
                              ...prev,
                              moodScore: prev.moodScore === option.score ? null : option.score,
                            }))
                          }
                        >
                          <span className="client-diary-v3__mood-emoji">{option.emoji}</span>
                          <span className="client-diary-v3__mood-text">{option.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <label className="client-diary-v3__field">
                    <span>Текст записи</span>
                    <textarea
                      value={draft.content}
                      onChange={(event) => setDraft((prev) => ({ ...prev, content: event.target.value }))}
                      placeholder={
                        "Что сейчас происходит внутри?\n\nЗаписи в дневнике остаются только вашими, если вы не решите поделиться ими с психологом."
                      }
                      rows={10}
                      maxLength={20000}
                    />
                  </label>

                  <div className="client-diary-v3__composer-footer">
                    <div className="client-diary-v3__composer-left">
                      <button
                        type="button"
                        className={`client-diary-v3__privacy-toggle ${draft.shareWithGrantedPsychologists ? "is-on" : ""}`}
                        onClick={() =>
                          setDraft((prev) => ({
                            ...prev,
                            shareWithGrantedPsychologists: !prev.shareWithGrantedPsychologists,
                          }))
                        }
                      >
                        <span className="client-diary-v3__privacy-track">
                          <span className="client-diary-v3__privacy-thumb" />
                        </span>
                        <span className="client-diary-v3__privacy-text">
                          {draft.shareWithGrantedPsychologists ? (
                            <>
                              Открыто <strong>психологу</strong>
                            </>
                          ) : (
                            <>
                              Только <strong>для меня</strong>
                            </>
                          )}
                        </span>
                      </button>
                    </div>

                    <div className="client-diary-v3__composer-actions">
                      <div className="client-diary-v3__composer-hint">
                        {draft.content.trim().length} / 20000
                        {draft.moodScore ? ` · ${diaryMoodBadge(draft.moodScore)}` : ""}
                      </div>
                      <div className="client-diary-v3__composer-buttons">
                        <button type="button" className="btn-sm" onClick={handleResetDraft} disabled={saving || deleting}>
                          Сбросить
                        </button>
                        {isCreating && sortedEntries.length ? (
                          <button
                            type="button"
                            className="btn-sm"
                            onClick={() => handleSelectEntry(sortedEntries[0])}
                            disabled={saving || deleting}
                          >
                            Отменить
                          </button>
                        ) : null}
                        <button type="submit" className="btn-sm primary" disabled={saving || deleting}>
                          {saving ? "Сохранение…" : isCreating ? "Сохранить запись" : "Сохранить изменения"}
                        </button>
                      </div>
                    </div>
                  </div>

                  {!accesses.length ? (
                    <div className="client-diary-v3__composer-note">
                      Сейчас нет выданных доступов: даже при включённом переключателе запись останется личной, пока вы не откроете дневник психологу.
                    </div>
                  ) : null}
                  </form>

                <div className="client-diary-v3__entries-head">
                  <div>
                    <div className="client-diary-v3__section-label">Последние записи</div>
                  </div>
                </div>

                <div className="client-diary-v3__entries">
                  {entriesLoading ? <p className="empty-note">Загрузка записей…</p> : null}
                  {!entriesLoading && entriesError ? <p className="empty-note">{entriesError}</p> : null}
                  {!entriesLoading && !entriesError && !sortedEntries.length ? (
                    <div className="client-diary-v3__empty">
                      <p className="empty-note">Записей пока нет. Начните с короткого чек-ина или полноценной заметки.</p>
                      <button
                        type="button"
                        className="btn-sm primary"
                        onClick={handleCreate}
                        disabled={diaryUnavailable}
                        title={diaryUnavailable ? "Дневник недоступен на сервере" : undefined}
                      >
                        Создать первую запись
                      </button>
                    </div>
                  ) : null}

                  {!entriesLoading &&
                    !entriesError &&
                    sortedEntries.map((entry) => {
                      const isSelected = !isCreating && String(selectedEntryId) === String(entry.id);
                      const visiblePsychologistName = accesses[0]?.psychologistName || "ваш психолог";
                      return (
                        <article
                          key={entry.id || `${entry.title}-${entry.createdAt}`}
                          className={`client-diary-v3__entry-card ${entry.shareWithGrantedPsychologists ? "is-shared" : ""} ${isSelected ? "is-selected" : ""}`}
                        >
                          <div className="client-diary-v3__entry-top">
                            <button
                              type="button"
                              className="client-diary-v3__entry-main"
                              onClick={() => handleSelectEntry(entry)}
                            >
                              <div className="client-diary-v3__entry-mood">
                                {entry.moodBadge ? entry.moodBadge.split(" ")[0] : "📝"}
                              </div>
                              <div className="client-diary-v3__entry-heading">
                                <div className="client-diary-v3__entry-weekday">
                                  {capitalizeFirst(formatWeekday(entry.entryDate || entry.createdAt))}
                                </div>
                                <div className="client-diary-v3__entry-date">{formatEntryDate(entry)}</div>
                              </div>
                            </button>

                            <div className="client-diary-v3__entry-badges">
                              <span
                                className={`client-diary-v3__badge ${entry.shareWithGrantedPsychologists ? "is-shared" : "is-private"}`}
                              >
                                {entry.shareWithGrantedPsychologists ? "Открыто психологу" : "Только для меня"}
                              </span>
                            </div>
                          </div>

                          <div className={`client-diary-v3__entry-body ${!isSelected ? "is-clamped" : ""}`}>
                            {trimText(entry.content || entry.text, 280)}
                          </div>

                          <div className="client-diary-v3__entry-footer">
                            <div className="client-diary-v3__entry-actions">
                              <button type="button" className="client-diary-v3__entry-action" onClick={() => handleSelectEntry(entry)}>
                                Редактировать
                              </button>
                              <button
                                type="button"
                                className="client-diary-v3__entry-action"
                                onClick={() => handleDelete(entry)}
                                disabled={deleting}
                              >
                                Удалить
                              </button>
                            </div>
                            <div className="client-diary-v3__entry-side">
                              {entry.shareWithGrantedPsychologists && accesses.length ? (
                                <span>{`Видит ${visiblePsychologistName}`}</span>
                              ) : (
                                <button
                                  type="button"
                                  className="client-diary-v3__entry-action client-diary-v3__entry-action--share"
                                  onClick={() => handleToggleEntryShare(entry)}
                                  disabled={!accesses.length}
                                >
                                  Открыть психологу
                                </button>
                              )}
                              {entry.shareWithGrantedPsychologists && accesses.length ? (
                                <button
                                  type="button"
                                  className="client-diary-v3__entry-action client-diary-v3__entry-action--share"
                                  onClick={() => handleToggleEntryShare(entry)}
                                  disabled={!accesses.length}
                                >
                                  Скрыть
                                </button>
                              ) : null}
                            </div>
                          </div>
                        </article>
                      );
                    })}
                </div>
                </section>

                <aside className="client-diary-v3__sidebar">
                <section className="client-diary-v3__side-card client-diary-v3__streak-card">
                  <div className="client-diary-v3__streak-icon">🔥</div>
                  <div className="client-diary-v3__streak-value">{streakCount}</div>
                  <div className="client-diary-v3__streak-label">дней подряд</div>
                  <div className="client-diary-v3__streak-bar">
                    {streakWindow.map((item) => (
                      <button
                        key={item.key}
                        type="button"
                        className={`client-diary-v3__streak-day ${item.hasEntry ? "is-done" : ""} ${item.isToday ? "is-today" : ""}`}
                        onClick={() => handleSelectCalendarDay({ date: item.date })}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </section>

                <section className="client-diary-v3__side-card">
                  <div className="client-diary-v3__side-head">
                    <h2>{calendarTitle}</h2>
                    <div className="client-diary-v3__calendar-nav">
                      <button type="button" onClick={() => setCalendarMonth((prev) => shiftMonth(prev, -1) || prev)}>
                        ←
                      </button>
                      <button type="button" onClick={() => setCalendarMonth((prev) => shiftMonth(prev, 1) || prev)}>
                        →
                      </button>
                    </div>
                  </div>
                  <div className="client-diary-v3__calendar-grid">
                    {["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"].map((label) => (
                      <div key={label} className="client-diary-v3__calendar-label">{label}</div>
                    ))}
                    {calendarCells.map((cell) => (
                      <button
                        key={cell.key}
                        type="button"
                        className={`client-diary-v3__calendar-day ${cell.inMonth ? "" : "is-muted"} ${cell.key === todayKey ? "is-today" : ""} ${entriesByDay.has(cell.key) ? "has-entry" : ""}`}
                        onClick={() => handleSelectCalendarDay(cell)}
                      >
                        {cell.day}
                      </button>
                    ))}
                  </div>
                </section>

                <section className="client-diary-v3__side-card client-diary-v3__share-card">
                  <div className="client-diary-v3__share-head">
                    <div className="client-diary-v3__share-icon">👁</div>
                    <div>
                      <div className="client-diary-v3__share-title">Доступ для психолога</div>
                      <div className="client-diary-v3__share-subtitle">
                        {accesses.length
                          ? `${accesses.length === 1 ? accesses[0]?.psychologistName || "Психолог" : `${accesses.length} специалистов`} видит только отмеченные открытыми записи.`
                          : "Никому ещё не выдан доступ к дневнику. Остальные записи остаются только вашими."}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className={`client-diary-v3__share-toggle ${shareToolsOpen ? "is-on" : ""}`}
                    onClick={() => setShareToolsOpen((prev) => !prev)}
                  >
                    <span className="client-diary-v3__share-toggle-label">Разрешить доступ к дневнику</span>
                    <span className="client-diary-v3__privacy-track">
                      <span className="client-diary-v3__privacy-thumb" />
                    </span>
                  </button>

                  {shareToolsOpen ? (
                    <div className="client-diary-v3__share-manage">
                      <label className="client-diary-v3__field">
                        <span>Выдать доступ</span>
                        <select
                          value={grantPsychologistId}
                          onChange={(event) => setGrantPsychologistId(event.target.value)}
                          disabled={diaryUnavailable || grantingAccess || psychologistsLoading || !availablePsychologists.length}
                        >
                          <option value="">{psychologistsLoading ? "Загружаем психологов…" : "Выбрать психолога"}</option>
                          {availablePsychologists.map((item) => (
                            <option key={item.id} value={item.id}>{item.name}</option>
                          ))}
                        </select>
                      </label>
                      <label className="client-diary-v3__field">
                        <span>Комментарий</span>
                        <input
                          type="text"
                          value={grantComment}
                          onChange={(event) => setGrantComment(event.target.value)}
                          placeholder="Комментарий к доступу (необязательно)"
                          maxLength={500}
                          disabled={grantingAccess}
                        />
                      </label>
                      <div className="client-diary-v3__share-actions">
                        <button type="button" className="btn-sm primary" onClick={handleGrantAccess} disabled={grantingAccess || !grantPsychologistId}>
                          {grantingAccess ? "Выдаём…" : "Выдать доступ"}
                        </button>
                        <button type="button" className="btn-sm" onClick={() => handleShareAllEntries(true)} disabled={bulkSharing || !sortedEntries.length}>
                          Открыть все
                        </button>
                        <button type="button" className="btn-sm" onClick={() => handleShareAllEntries(false)} disabled={bulkSharing || !sortedEntries.length}>
                          Скрыть все
                        </button>
                      </div>

                      <div className="client-diary-v3__side-list">
                        {accessesLoading ? <p className="empty-note">Проверяем доступы…</p> : null}
                        {!accessesLoading && diaryUnavailable ? (
                          <p className="empty-note">Сервис дневника недоступен: список доступов сейчас не загружается.</p>
                        ) : null}
                        {!accessesLoading && !diaryUnavailable && !accesses.length ? (
                          <p className="empty-note">Пока ни одному психологу не выдан доступ к дневнику.</p>
                        ) : null}
                        {!accessesLoading &&
                          !diaryUnavailable &&
                          accesses.map((access) => (
                            <div className="client-diary-v3__side-item" key={access.id || access.psychologistUserId}>
                              <div>
                                <div className="client-diary-v3__side-item-title">{access.psychologistName}</div>
                                <div className="client-diary-v3__side-item-text">
                                  {access.comment || `Доступ выдан ${formatRuDateTime(access.grantedAt)}`}
                                </div>
                              </div>
                              <button
                                type="button"
                                className="client-diary-v3__inline-action"
                                onClick={() => handleRevokeAccess(access.psychologistUserId)}
                                disabled={revokingAccessId === String(access.psychologistUserId)}
                              >
                                {revokingAccessId === String(access.psychologistUserId) ? "..." : "Отозвать"}
                              </button>
                            </div>
                          ))}
                      </div>
                    </div>
                  ) : null}
                </section>
                </aside>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
